import { superAdminApi } from "../authService";
import {
  ApiResponse,
  PaginatedApiResponse,
  PaginationParams,
} from "../../types/api";
import {
  SupportTicket,
  SupportTicketCategory,
  SupportTicketMessage,
  SupportTicketStatus,
} from "../supportTicketService";

const RESOURCE = "/super-admin/support/tickets";

export interface AdminSupportTicketListParams extends PaginationParams {
  status?: SupportTicketStatus | "";
  category?: SupportTicketCategory | "";
  satisfaction?: "yes" | "no" | "";
  tenant_id?: number;
  assigned_to?: number;
  unassigned?: boolean | 1 | 0;
  search?: string;
  hide_snoozed?: boolean | 1 | 0;
  has_recurring_theme?: boolean | 1 | 0;
  no_agent_reply?: boolean | 1 | 0;
  sort?: "created_at" | "sla";
  order?: "asc" | "desc";
}

export interface AdminSupportRecurringTheme {
  subject: string;
  category: string;
  count: number;
}

export interface AdminSupportSummary {
  awaiting_tenant: number;
  satisfaction_no_open: number;
  unassigned_open: number;
  my_open: number;
  avg_first_response_hours: number | null;
  by_status: Record<SupportTicketStatus, number>;
  by_category: Partial<Record<SupportTicketCategory, number>>;
  recurring_themes?: AdminSupportRecurringTheme[];
  suggested_next_adj_id?: string | null;
}

export type SupportAgentOption = {
  id: number;
  name: string;
  email: string;
};

export const getAdminSupportAgents = async (): Promise<
  ApiResponse<SupportAgentOption[]>
> => {
  const response = await superAdminApi.get<ApiResponse<SupportAgentOption[]>>(
    "/super-admin/support/agents"
  );

  return response.data;
};

export const getAdminSupportTickets = async (
  params?: AdminSupportTicketListParams
): Promise<PaginatedApiResponse<SupportTicket>> => {
  const response = await superAdminApi.get<PaginatedApiResponse<SupportTicket>>(
    RESOURCE,
    { params }
  );

  return response.data;
};

export const getAdminSupportTicket = async (
  id: number
): Promise<ApiResponse<SupportTicket>> => {
  const response = await superAdminApi.get<ApiResponse<SupportTicket>>(
    `${RESOURCE}/${id}`
  );

  return response.data;
};

export const updateAdminSupportTicket = async (
  id: number,
  payload: {
    status?: SupportTicketStatus;
    assigned_to_super_admin_id?: number | null;
    snooze_hours?: 1 | 4 | 24;
    snoozed_until?: string | null;
  }
): Promise<ApiResponse<SupportTicket>> => {
  const response = await superAdminApi.patch<ApiResponse<SupportTicket>>(
    `${RESOURCE}/${id}`,
    payload
  );

  return response.data;
};

export const replyAdminSupportTicket = async (
  id: number,
  payload: {
    body: string;
    is_internal_note?: boolean;
    attachments?: File[];
  }
): Promise<ApiResponse<SupportTicketMessage>> => {
  const formData = new FormData();
  formData.append("body", payload.body);
  formData.append("is_internal_note", payload.is_internal_note ? "1" : "0");
  (payload.attachments || []).forEach((file) => {
    formData.append("attachments[]", file);
  });

  const response = await superAdminApi.post<ApiResponse<SupportTicketMessage>>(
    `${RESOURCE}/${id}/messages`,
    formData,
    { headers: { "Content-Type": "multipart/form-data" } }
  );

  return response.data;
};

export const getAdminSupportSummary = async (): Promise<
  ApiResponse<AdminSupportSummary>
> => {
  const response = await superAdminApi.get<ApiResponse<AdminSupportSummary>>(
    `${RESOURCE}/summary`
  );

  return response.data;
};

export const downloadAdminSupportAttachment = async (
  ticketId: number,
  attachmentId: number,
  filename: string
): Promise<void> => {
  const blob = await fetchAdminSupportAttachmentBlob(ticketId, attachmentId);
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.URL.revokeObjectURL(url);
};

export const fetchAdminSupportAttachmentBlob = async (
  ticketId: number,
  attachmentId: number
): Promise<Blob> => {
  const response = await superAdminApi.get(
    `${RESOURCE}/${ticketId}/attachments/${attachmentId}/download`,
    { responseType: "blob" }
  );

  return response.data instanceof Blob
    ? response.data
    : new Blob([response.data as BlobPart]);
};
